import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Bot, FolderOpen, FileText, Upload, Trash2, 
  BookOpen, Loader2, Mic, CheckCircle, X, Download,
  Calendar, Clock, Users, Globe, ListChecks, Edit2, Plus, UserPlus, Search
} from "lucide-react";
import HermesChat from "@/components/HermesChat";
import { format, parseISO } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { LANG_NAMES } from "@/config/languages";
const SUPPORTED_LANGUAGES = { ...LANG_NAMES };

export default function MeetAIPanel({ meeting, isOpen, onClose, onUpdate }) {
  const [activeTab, setActiveTab] = useState("board");
  // Chat handled by HermesChat
  const [folder, setFolder] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLearning, setIsLearning] = useState({});
  const [minutes, setMinutes] = useState(null);
  const [minutesInput, setMinutesInput] = useState("");
  const [draftMinutes, setDraftMinutes] = useState(null);
  const [isEditingMinutes, setIsEditingMinutes] = useState(false);
  const [editedMinutesContent, setEditedMinutesContent] = useState("");
  const [isSavingMinutes, setIsSavingMinutes] = useState(false);
  const [submissions, setSubmissions] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmissions, setShowSubmissions] = useState(false);
  const [consolidatedMinutes, setConsolidatedMinutes] = useState(null);
  const fileInputRef = useRef(null);
  
  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [isInviteMode, setIsInviteMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editAgenda, setEditAgenda] = useState([]);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  
  // Get current user ID
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(payload.user_id);
      } catch (e) {}
    }
  }, []);

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (meeting && isOpen) {
      fetchFolder();
      fetchMinutes();
      fetchDraftMinutes();
      fetchSubmissions();
      fetchConsolidatedMinutes();
      // Reset edit mode when meeting changes
      setIsEditMode(false);
      setEditForm({
        title: meeting.title || "",
        description: meeting.description || "",
        scheduled_at: meeting.scheduled_at ? format(parseISO(meeting.scheduled_at), "yyyy-MM-dd'T'HH:mm") : "",
        duration_minutes: meeting.duration_minutes || 60,
        default_language: meeting.default_language || "en"
      });
      setEditAgenda(meeting.agenda?.length ? [...meeting.agenda] : [""]);
    }
  }, [meeting, isOpen]);

  // Edit meeting functions
  const startEdit = () => {
    setEditForm({
      title: meeting.title || "",
      description: meeting.description || "",
      scheduled_at: meeting.scheduled_at ? format(parseISO(meeting.scheduled_at), "yyyy-MM-dd'T'HH:mm") : "",
      duration_minutes: meeting.duration_minutes || 60,
      default_language: meeting.default_language || "en"
    });
    setEditAgenda(meeting.agenda?.length ? [...meeting.agenda] : [""]);
    setIsEditMode(true);
    setIsInviteMode(false);
  };

  const startInvite = () => {
    setEditForm({
      title: meeting.title || "",
      description: meeting.description || "",
      scheduled_at: meeting.scheduled_at ? format(parseISO(meeting.scheduled_at), "yyyy-MM-dd'T'HH:mm") : "",
      duration_minutes: meeting.duration_minutes || 60,
      default_language: meeting.default_language || "en"
    });
    setEditAgenda(meeting.agenda?.length ? [...meeting.agenda] : [""]);
    setIsEditMode(true);
    setIsInviteMode(true);
  };

  const cancelEdit = () => {
    setIsEditMode(false);
    setIsInviteMode(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  const saveEdit = async () => {
    setIsSavingEdit(true);
    try {
      const updates = {
        title: editForm.title,
        description: editForm.description,
        scheduled_at: editForm.scheduled_at,
        duration_minutes: parseInt(editForm.duration_minutes),
        default_language: editForm.default_language,
        agenda: editAgenda.filter(a => a.trim())
      };

      await axios.put(`${API}/comms/meetings/${meeting.id}`, updates, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });

      toast.success("Meeting updated successfully");
      setIsEditMode(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update meeting");
    }
    setIsSavingEdit(false);
  };

  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/comms/chat/users/search?q=${encodeURIComponent(query)}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      // Filter out users already in meeting
      const existingIds = meeting.participant_details?.map(p => p.user_id) || [];
      setSearchResults(res.data.filter(u => !existingIds.includes(u.user_id)));
    } catch (err) {
      console.error("Search failed");
    }
  };

  const addParticipant = async (user) => {
    try {
      await axios.put(`${API}/comms/meetings/${meeting.id}`, {
        add_participants: [user.user_id]
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      toast.success(`${user.name} added to meeting`);
      setSearchQuery("");
      setSearchResults([]);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error("Failed to add participant");
    }
  };

  const removeParticipant = async (userId) => {
    try {
      await axios.put(`${API}/comms/meetings/${meeting.id}`, {
        remove_participants: [userId]
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      toast.success("Participant removed");
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error("Failed to remove participant");
    }
  };

  const addAgendaItem = () => {
    setEditAgenda([...editAgenda, ""]);
  };

  const updateAgendaItem = (index, value) => {
    const updated = [...editAgenda];
    updated[index] = value;
    setEditAgenda(updated);
  };

  const removeAgendaItem = (index) => {
    if (editAgenda.length > 1) {
      setEditAgenda(editAgenda.filter((_, i) => i !== index));
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getStatusColor = (participant) => {
    if (meeting.confirmed_participants?.includes(participant.user_id)) return "bg-green-100 border-green-300";
    if (meeting.declined_participants?.includes(participant.user_id)) return "bg-red-100 border-red-300";
    return "bg-amber-100 border-amber-300";
  };

  const getStatusLabel = (participant) => {
    if (meeting.confirmed_participants?.includes(participant.user_id)) return "Confirmed";
    if (meeting.declined_participants?.includes(participant.user_id)) return "Declined";
    return "Pending";
  };

  const isCreator = currentUserId === meeting?.creator_id;
  const canEdit = isCreator && meeting?.status === "scheduled";

  const fetchFolder = async () => {
    try {
      const res = await axios.get(`${API}/comms/meetings/${meeting.id}/folder`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setFolder(res.data);
    } catch (err) {
      // Folder might not exist yet
      setFolder(null);
    }
  };

  const fetchMinutes = async () => {
    try {
      const res = await axios.get(`${API}/comms/meetings/${meeting.id}/minutes`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setMinutes(res.data);
    } catch (err) {
      setMinutes(null);
    }
  };

  const fetchDraftMinutes = async () => {
    try {
      const res = await axios.get(`${API}/comms/meetings/${meeting.id}/minutes/draft`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setDraftMinutes(res.data);
      setEditedMinutesContent(res.data.content || "");
    } catch (err) {
      setDraftMinutes(null);
    }
  };

  const saveDraftMinutes = async () => {
    setIsSavingMinutes(true);
    try {
      await axios.put(`${API}/comms/meetings/${meeting.id}/minutes/draft`, 
        { content: editedMinutesContent },
        {
          headers: { ...getHeaders(), "Content-Type": "application/json" },
          withCredentials: true
        }
      );
      toast.success("Draft minutes saved");
      setIsEditingMinutes(false);
      fetchDraftMinutes();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error("Failed to save minutes");
    } finally {
      setIsSavingMinutes(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const res = await axios.get(`${API}/comms/meetings/${meeting.id}/minutes/submissions`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setSubmissions(res.data);
    } catch (err) {
      setSubmissions(null);
    }
  };

  const fetchConsolidatedMinutes = async () => {
    try {
      const res = await axios.get(`${API}/comms/meetings/${meeting.id}/minutes/consolidated`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setConsolidatedMinutes(res.data);
    } catch (err) {
      setConsolidatedMinutes(null);
    }
  };

  const submitMinutesInput = async (hasInputs) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/comms/meetings/${meeting.id}/minutes/submit`,
        { 
          content: editedMinutesContent, 
          has_inputs: hasInputs 
        },
        {
          headers: { ...getHeaders(), "Content-Type": "application/json" },
          withCredentials: true
        }
      );
      toast.success(hasInputs ? "Input submitted with changes" : "Submitted without inputs");
      setIsEditingMinutes(false);
      fetchSubmissions();
      fetchDraftMinutes();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error("Failed to submit input");
    } finally {
      setIsSubmitting(false);
    }
  };

  const triggerConsolidation = async () => {
    try {
      await axios.post(`${API}/comms/meetings/${meeting.id}/minutes/consolidate`, {}, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success("MeetAI is consolidating all inputs");
      fetchConsolidatedMinutes();
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to trigger consolidation");
    }
  };

  const getMinutesStatusBadge = () => {
    const status = draftMinutes?.minutes_status || folder?.minutes_status || "pending";
    if (status === "pending") {
      return <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-700">No Minutes</span>;
    }
    if (status === "collecting_inputs") {
      return <span className="px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-700">Collecting Inputs</span>;
    }
    if (status === "consolidating") {
      return <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">Consolidating</span>;
    }
    if (status === "in_review") {
      return <span className="px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-700">In Review</span>;
    }
    if (status === "finalized") {
      return <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">Minutes Completed</span>;
    }
    return null;
  };

  const getSubmissionStatusBadge = (submission) => {
    if (submission.status === "pending") {
      return <span className="px-2 py-0.5 rounded text-xs bg-slate-200 text-slate-600">Pending</span>;
    }
    if (submission.has_inputs === true) {
      // RED - has inputs
      return <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700 font-medium">With Inputs</span>;
    }
    if (submission.has_inputs === false) {
      // GREEN - no inputs
      return <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700 font-medium">No Inputs</span>;
    }
    return null;
  };

  const uploadDocument = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);

        await axios.post(`${API}/comms/meetings/${meeting.id}/folder/documents`, formData, {
          headers: getHeaders(),
          withCredentials: true
        });
      }
      toast.success(`${files.length} document(s) uploaded`);
      fetchFolder();
    } catch (err) {
      toast.error("Failed to upload document");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const learnDocument = async (docId) => {
    setIsLearning(prev => ({ ...prev, [docId]: true }));
    try {
      await axios.post(`${API}/comms/meetings/${meeting.id}/folder/documents/${docId}/learn`, {}, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success("MeetAI has learned the document");
      fetchFolder();
    } catch (err) {
      toast.error("Failed to learn document");
    } finally {
      setIsLearning(prev => ({ ...prev, [docId]: false }));
    }
  };

  const deleteDocument = async (docId) => {
    try {
      await axios.delete(`${API}/comms/meetings/${meeting.id}/folder/documents/${docId}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success("Document deleted");
      fetchFolder();
    } catch (err) {
      toast.error("Failed to delete document");
    }
  };

  
  // Note: submitMinutesInput is now defined earlier (around line 136) with hasInputs parameter

  const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!meeting) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
              <FolderOpen className="w-4 h-4 text-white" />
            </div>
            <div>
              <span>MeetAI & Meeting Folder</span>
              <p className="text-xs font-normal text-slate-500">{meeting.title}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="board" className="gap-1 text-xs" data-testid="tab-meeting-board">
              <ListChecks className="w-4 h-4" />
              Board
            </TabsTrigger>
            <TabsTrigger value="chat" className="gap-1 text-xs" data-testid="tab-chat">
              <Bot className="w-4 h-4" />
              Chat
            </TabsTrigger>
            <TabsTrigger value="documents" className="gap-1 text-xs" data-testid="tab-documents">
              <FolderOpen className="w-4 h-4" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="minutes" className="gap-1 text-xs" data-testid="tab-minutes">
              <FileText className="w-4 h-4" />
              Minutes
            </TabsTrigger>
          </TabsList>

          {/* Meeting Board Tab */}
          <TabsContent value="board" className="mt-4">
            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
              {isEditMode ? (
                /* Edit Mode */
                <div className="space-y-3">
                  <div className="flex items-center justify-between sticky top-0 bg-white py-2 z-10">
                    <h3 className="font-semibold text-slate-800">{isInviteMode ? "Invite Participants" : "Edit Meeting"}</h3>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={cancelEdit}>Cancel</Button>
                      <Button size="sm" onClick={saveEdit} disabled={isSavingEdit} className="bg-violet-600 hover:bg-violet-500">
                        {isSavingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Add Participants - Show at top when in invite mode */}
                  {isInviteMode && (
                    <div className="bg-violet-50 border border-violet-200 p-3 rounded-lg">
                      <div className="flex items-center gap-2 text-violet-700 mb-2">
                        <UserPlus className="w-4 h-4" />
                        <span className="font-medium text-sm">Search & Add Participants</span>
                      </div>
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            searchUsers(e.target.value);
                          }}
                          placeholder="Search by name or email..."
                          className="pl-9 h-9 bg-white"
                          autoFocus
                          data-testid="invite-search-input"
                        />
                        {searchResults.length > 0 && (
                          <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-auto">
                            {searchResults.map((user) => (
                              <div
                                key={user.user_id}
                                onClick={() => addParticipant(user)}
                                className="flex items-center gap-2 p-2.5 hover:bg-violet-50 cursor-pointer border-b last:border-b-0"
                              >
                                <Avatar className="w-8 h-8">
                                  <AvatarImage src={user.picture} />
                                  <AvatarFallback className="text-xs bg-violet-100 text-violet-600">
                                    {getInitials(user.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1">
                                  <p className="text-sm font-medium">{user.name}</p>
                                  <p className="text-xs text-slate-500">{user.email}</p>
                                </div>
                                <Plus className="w-4 h-4 text-violet-500" />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Current Participants */}
                      <div className="mt-3">
                        <p className="text-xs text-slate-500 mb-2">Current participants ({meeting.participant_details?.length || 0})</p>
                        <div className="flex flex-wrap gap-1.5">
                          {meeting.participant_details?.map((p) => (
                            <div key={p.user_id} className="flex items-center gap-1.5 bg-white border rounded-full px-2 py-1">
                              <Avatar className="w-5 h-5">
                                <AvatarFallback className="text-[10px] bg-slate-100">{getInitials(p.name)}</AvatarFallback>
                              </Avatar>
                              <span className="text-xs text-slate-700">{p.name}</span>
                              {p.user_id !== meeting.creator_id && (
                                <button onClick={() => removeParticipant(p.user_id)} className="text-red-400 hover:text-red-600">
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Meeting Details Form - Collapsible when in invite mode */}
                  {isInviteMode ? (
                    <details className="bg-slate-50 rounded-lg">
                      <summary className="p-2 text-sm text-slate-600 cursor-pointer hover:bg-slate-100">
                        Edit other meeting details...
                      </summary>
                      <div className="p-3 pt-0 grid grid-cols-2 gap-2">
                        <div className="col-span-2">
                          <Label className="text-xs">Title</Label>
                          <Input value={editForm.title} onChange={(e) => setEditForm({...editForm, title: e.target.value})} className="mt-1 h-8" />
                        </div>
                        <div>
                          <Label className="text-xs">Date & Time</Label>
                          <Input type="datetime-local" value={editForm.scheduled_at} onChange={(e) => setEditForm({...editForm, scheduled_at: e.target.value})} className="mt-1 h-8" />
                        </div>
                        <div>
                          <Label className="text-xs">Duration</Label>
                          <Input type="number" value={editForm.duration_minutes} onChange={(e) => setEditForm({...editForm, duration_minutes: e.target.value})} className="mt-1 h-8" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Language</Label>
                          <Select value={editForm.default_language} onValueChange={(v) => setEditForm({...editForm, default_language: v})}>
                            <SelectTrigger className="mt-1 h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => (
                                <SelectItem key={code} value={code}>{name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </details>
                  ) : (
                    /* Full Edit Form when not in invite mode */
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <Label className="text-xs">Meeting Title</Label>
                          <Input
                            value={editForm.title}
                            onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                            className="mt-1 h-8"
                          />
                        </div>
                        
                        <div className="col-span-2">
                          <Label className="text-xs">Description</Label>
                          <Textarea
                            value={editForm.description}
                            onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                            className="mt-1"
                            rows={2}
                          />
                        </div>
                        
                        <div>
                          <Label className="text-xs">Date & Time</Label>
                          <Input
                            type="datetime-local"
                            value={editForm.scheduled_at}
                            onChange={(e) => setEditForm({...editForm, scheduled_at: e.target.value})}
                            className="mt-1 h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Duration (min)</Label>
                          <Input
                            type="number"
                            value={editForm.duration_minutes}
                            onChange={(e) => setEditForm({...editForm, duration_minutes: e.target.value})}
                            className="mt-1 h-8"
                          />
                        </div>
                        
                        <div className="col-span-2">
                          <Label className="text-xs">Default Language</Label>
                          <Select value={editForm.default_language} onValueChange={(v) => setEditForm({...editForm, default_language: v})}>
                            <SelectTrigger className="mt-1 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => (
                                <SelectItem key={code} value={code}>{name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Agenda Editor */}
                      <div className="border-t pt-3">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-xs font-medium">Agenda Items</Label>
                          <Button size="sm" variant="ghost" onClick={addAgendaItem} className="h-6 text-xs text-violet-600">
                            <Plus className="w-3 h-3 mr-1" /> Add
                          </Button>
                        </div>
                        <div className="space-y-1.5">
                          {editAgenda.map((item, idx) => (
                            <div key={idx} className="flex gap-2 items-center">
                              <span className="text-xs text-slate-400 w-4">{idx + 1}.</span>
                              <Input
                                value={item}
                                onChange={(e) => updateAgendaItem(idx, e.target.value)}
                                placeholder={`Agenda item ${idx + 1}`}
                                className="flex-1 h-8"
                              />
                              {editAgenda.length > 1 && (
                                <Button size="sm" variant="ghost" onClick={() => removeAgendaItem(idx)} className="h-6 w-6 p-0 text-red-500">
                                  <X className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Add Participants (in full edit mode) */}
                      <div className="border-t pt-3">
                        <Label className="text-xs font-medium">Add Participants</Label>
                        <div className="mt-1 relative">
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <Input
                                value={searchQuery}
                                onChange={(e) => {
                                  setSearchQuery(e.target.value);
                                  searchUsers(e.target.value);
                                }}
                                placeholder="Search by name or email..."
                                className="pl-8 h-8"
                              />
                            </div>
                          </div>
                          {searchResults.length > 0 && (
                            <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-32 overflow-auto">
                              {searchResults.map((user) => (
                                <div
                                  key={user.user_id}
                                  onClick={() => addParticipant(user)}
                                  className="flex items-center gap-2 p-2 hover:bg-violet-50 cursor-pointer"
                                >
                                  <Avatar className="w-6 h-6">
                                    <AvatarImage src={user.picture} />
                                    <AvatarFallback className="text-xs bg-violet-100 text-violet-600">
                                      {getInitials(user.name)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <p className="text-sm font-medium">{user.name}</p>
                                    <p className="text-xs text-slate-500">{user.email}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                /* View Mode */
                <div className="space-y-3">
                  {/* Header with Edit Button */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-base text-slate-800 truncate">{meeting.title}</h3>
                      {meeting.description && (
                        <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{meeting.description}</p>
                      )}
                    </div>
                    {canEdit && (
                      <Button size="sm" variant="outline" onClick={startEdit} className="gap-1 h-7 ml-2" data-testid="edit-meeting-btn">
                        <Edit2 className="w-3 h-3" />
                        Edit
                      </Button>
                    )}
                  </div>
                  
                  {/* Meeting Details - Compact Grid */}
                  <div className="grid grid-cols-4 gap-2">
                    <div className="bg-slate-50 p-2 rounded">
                      <div className="flex items-center gap-1 text-slate-500 mb-1">
                        <Calendar className="w-3 h-3" />
                        <span className="text-[10px] font-medium">Date</span>
                      </div>
                      <p className="text-xs font-semibold text-slate-800">
                        {meeting.scheduled_at ? format(parseISO(meeting.scheduled_at), "MMM d, yyyy") : "Not set"}
                      </p>
                      <p className="text-[10px] text-slate-500">
                        {meeting.scheduled_at ? format(parseISO(meeting.scheduled_at), "HH:mm") : ""}
                      </p>
                    </div>
                    
                    <div className="bg-slate-50 p-2 rounded">
                      <div className="flex items-center gap-1 text-slate-500 mb-1">
                        <Clock className="w-3 h-3" />
                        <span className="text-[10px] font-medium">Duration</span>
                      </div>
                      <p className="text-xs font-semibold text-slate-800">
                        {meeting.duration_minutes || 60} min
                      </p>
                    </div>
                    
                    <div className="bg-slate-50 p-2 rounded">
                      <div className="flex items-center gap-1 text-slate-500 mb-1">
                        <Globe className="w-3 h-3" />
                        <span className="text-[10px] font-medium">Language</span>
                      </div>
                      <p className="text-xs font-semibold text-slate-800">
                        {SUPPORTED_LANGUAGES[meeting.default_language] || "English"}
                      </p>
                    </div>
                    
                    <div className="bg-slate-50 p-2 rounded">
                      <div className="flex items-center gap-1 text-slate-500 mb-1">
                        <Users className="w-3 h-3" />
                        <span className="text-[10px] font-medium">Attendees</span>
                      </div>
                      <p className="text-xs font-semibold text-slate-800">
                        {meeting.participant_details?.length || 0} invited
                      </p>
                      <p className="text-[10px] text-green-600">
                        {meeting.confirmed_participants?.length || 0} confirmed
                      </p>
                    </div>
                  </div>
                  
                  {/* Agenda - Compact */}
                  <div className="bg-violet-50 border border-violet-100 p-2.5 rounded">
                    <div className="flex items-center gap-1.5 text-violet-700 mb-2">
                      <ListChecks className="w-3.5 h-3.5" />
                      <span className="text-xs font-medium">Agenda</span>
                    </div>
                    {meeting.agenda?.length > 0 ? (
                      <ol className="space-y-1">
                        {meeting.agenda.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-1.5 text-xs">
                            <span className="w-4 h-4 bg-violet-200 text-violet-700 rounded-full flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">
                              {idx + 1}
                            </span>
                            <span className="text-slate-700">{item}</span>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <p className="text-xs text-slate-500 italic">No agenda items</p>
                    )}
                  </div>
                  
                  {/* Participants List - Compact */}
                  <div className="border-t pt-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5 text-slate-700">
                        <Users className="w-3.5 h-3.5" />
                        <span className="text-xs font-medium">Participants</span>
                      </div>
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={startInvite} className="text-violet-600 h-6 text-xs px-2" data-testid="invite-participants-btn">
                          <UserPlus className="w-3 h-3 mr-1" />
                          Invite
                        </Button>
                      )}
                    </div>
                    <div className="space-y-1.5 max-h-[120px] overflow-y-auto">
                      {meeting.participant_details?.map((p) => (
                        <div key={p.user_id} className={`flex items-center justify-between p-1.5 rounded border ${getStatusColor(p)}`}>
                          <div className="flex items-center gap-1.5">
                            <Avatar className="w-6 h-6">
                              <AvatarImage src={p.picture} />
                              <AvatarFallback className="text-[10px] bg-slate-200">
                                {getInitials(p.name)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-xs font-medium text-slate-800">{p.name}</p>
                              <p className="text-[10px] text-slate-500">@{p.username || p.email?.split('@')[0]}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                              getStatusLabel(p) === "Confirmed" ? "bg-green-200 text-green-700" :
                              getStatusLabel(p) === "Declined" ? "bg-red-200 text-red-700" :
                              "bg-amber-200 text-amber-700"
                            }`}>
                              {getStatusLabel(p)}
                            </span>
                            {canEdit && p.user_id !== meeting.creator_id && (
                              <Button size="sm" variant="ghost" onClick={() => removeParticipant(p.user_id)} className="h-5 w-5 p-0 text-red-500">
                                <X className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Chat Tab */}
          <TabsContent value="chat" className="mt-4">
            <div className="border rounded-lg overflow-hidden" style={{ height: "400px" }}>
              <HermesChat
                contextHint={"[MeetAI - meeting_id=" + meeting.id + "]"}
                title="MeetAI"
                icon={Bot}
                color="from-violet-500 to-purple-600"
                accentColor="text-violet-400"
                layout="tab"
                placeholder="Ask MeetAI about meeting documents..."
                sessionPrefix={"meetai_" + meeting.id}
              />
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="mt-4">
            <div className="space-y-4">
              {/* Meeting Folder Header */}
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <FolderOpen className="w-5 h-5 text-violet-600" />
                  <h3 className="font-semibold text-violet-800">Meeting Folder</h3>
                </div>
                <p className="text-xs text-violet-600">
                  Documents and minutes for this meeting. Only participants can access.
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">
                    {folder?.documents?.length || 0} document(s) in folder
                  </p>
                  <p className="text-xs text-slate-400">
                    Supported: PDF, DOCX, TXT, XLSX, CSV, MD, JSON
                  </p>
                </div>
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={uploadDocument}
                    className="hidden"
                    multiple
                    accept=".pdf,.docx,.txt,.md,.csv,.json,.xml,.html,.xlsx,.py,.js,.ts,.log,.rtf"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="gap-2"
                    data-testid="upload-doc-btn"
                  >
                    {isUploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    Upload Document
                  </Button>
                </div>
              </div>

              <ScrollArea className="h-48">
                {folder?.documents?.length > 0 ? (
                  <div className="space-y-2">
                    {/* Minutes Documents (Draft/Final) */}
                    {folder.documents.filter(d => d.name.includes('Minutes')).length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">
                          Meeting Minutes
                        </p>
                        {folder.documents.filter(d => d.name.includes('Minutes')).map((doc) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg mb-2"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <FileText className="w-5 h-5 text-green-600 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="font-medium text-sm text-green-800 truncate">
                                  {doc.name}
                                </p>
                                <p className="text-xs text-green-600">
                                  {doc.name.includes('Final') ? 'Finalized by MeetAI' : 'Draft - Awaiting review'}
                                </p>
                              </div>
                            </div>
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button variant="ghost" size="sm" className="text-green-600">
                                <Download className="w-4 h-4" />
                              </Button>
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Regular Documents */}
                    {folder.documents.filter(d => !d.name.includes('Minutes')).length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">
                          Uploaded Documents
                        </p>
                        {folder.documents.filter(d => !d.name.includes('Minutes')).map((doc) => (
                          <div
                            key={doc.id}
                            className={`flex items-center justify-between p-3 rounded-lg mb-2 ${
                              doc.learned 
                                ? 'bg-green-50 border border-green-200' 
                                : doc.format_warning 
                                  ? 'bg-amber-50 border border-amber-200'
                                  : 'bg-slate-50'
                            }`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <FileText className={`w-5 h-5 flex-shrink-0 ${
                                doc.learned ? 'text-green-600' : doc.format_warning ? 'text-amber-600' : 'text-slate-500'
                              }`} />
                              <div className="min-w-0">
                                <p className="font-medium text-sm text-slate-800 truncate">
                                  {doc.name}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {formatFileSize(doc.size)}
                                  {doc.learned && (
                                    <span className="ml-2 text-green-600">
                                      <CheckCircle className="w-3 h-3 inline mr-1" />
                                      Learned by MeetAI
                                    </span>
                                  )}
                                  {!doc.learned && doc.format_warning && (
                                    <span className="ml-2 text-amber-600 text-xs">
                                      ⚠️ Format may have limited support
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {!doc.learned && !doc.name.includes('Minutes') && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => learnDocument(doc.id)}
                                  disabled={isLearning[doc.id]}
                                  className="text-violet-600 hover:text-violet-700"
                                  title="MeetAI learns this document"
                                >
                                  {isLearning[doc.id] ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <BookOpen className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Button variant="ghost" size="sm">
                                  <Download className="w-4 h-4" />
                                </Button>
                              </a>
                              {!doc.name.includes('Minutes') && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteDocument(doc.id)}
                                  className="text-red-500 hover:text-red-600"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <FolderOpen className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">No documents in folder yet</p>
                    <p className="text-xs text-slate-400">Upload documents for MeetAI to learn before the meeting</p>
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Minutes Tab */}
          <TabsContent value="minutes" className="mt-4">
            <div className="space-y-4">
              {/* Status Header */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-800">Meeting Minutes</p>
                  <p className="text-xs text-slate-500">
                    {draftMinutes?.is_template 
                      ? "Template structure - will be populated after meeting" 
                      : "Click Edit to make changes"}
                  </p>
                </div>
                {getMinutesStatusBadge()}
              </div>

              {/* Draft Minutes Content */}
              {draftMinutes?.content ? (
                <>
                  {isEditingMinutes ? (
                    <div className="space-y-3">
                      <Textarea
                        value={editedMinutesContent}
                        onChange={(e) => setEditedMinutesContent(e.target.value)}
                        className="h-64 font-mono text-xs"
                        data-testid="edit-minutes-textarea"
                      />
                      
                      {/* Save Changes Row */}
                      <div className="flex gap-2">
                        <Button
                          onClick={saveDraftMinutes}
                          disabled={isSavingMinutes}
                          className="flex-1 bg-violet-500 hover:bg-violet-600"
                        >
                          {isSavingMinutes ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                          )}
                          Save Changes
                        </Button>
                        <Button
                          onClick={() => {
                            setIsEditingMinutes(false);
                            setEditedMinutesContent(draftMinutes.content);
                          }}
                          variant="outline"
                        >
                          Cancel
                        </Button>
                      </div>
                      
                      {/* Submit Input Row */}
                      <div className="border-t pt-3 mt-3">
                        <p className="text-xs text-slate-500 mb-2">After saving, submit your input for MeetAI consolidation:</p>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => submitMinutesInput(true)}
                            disabled={isSubmitting}
                            className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                            data-testid="submit-with-inputs-btn"
                          >
                            {isSubmitting ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-1" />
                            ) : null}
                            Send WITH Inputs
                          </Button>
                          <Button
                            onClick={() => submitMinutesInput(false)}
                            disabled={isSubmitting}
                            className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                            data-testid="submit-without-inputs-btn"
                          >
                            Send WITHOUT Inputs
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <ScrollArea className="h-64 border rounded-lg p-4 bg-white">
                        <div className="prose prose-sm max-w-none">
                          <pre className="whitespace-pre-wrap text-xs text-slate-700 font-sans leading-relaxed">
                            {draftMinutes.content}
                          </pre>
                        </div>
                      </ScrollArea>
                      <Button
                        onClick={() => setIsEditingMinutes(true)}
                        variant="outline"
                        className="w-full"
                        data-testid="edit-minutes-btn"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Edit & Submit Input
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">Loading minutes template...</p>
                </div>
              )}

              {/* Participant Submissions Section */}
              {submissions && submissions.submissions?.length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-medium text-slate-700">Participant Submissions</p>
                      <p className="text-xs text-slate-500">
                        {submissions.submitted_count}/{submissions.total_participants} submitted
                        {submissions.input_deadline && (
                          <> • Deadline: {new Date(submissions.input_deadline).toLocaleDateString()}</>
                        )}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowSubmissions(!showSubmissions)}
                    >
                      {showSubmissions ? "Hide" : "Show All"}
                    </Button>
                  </div>
                  
                  {/* Submission Status Grid */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {submissions.submissions.map((sub) => (
                      <div 
                        key={sub.user_id}
                        className={`px-3 py-1.5 rounded-lg border text-xs ${
                          sub.has_inputs === true 
                            ? "bg-red-50 border-red-200 text-red-700" 
                            : sub.has_inputs === false 
                            ? "bg-green-50 border-green-200 text-green-700"
                            : "bg-slate-50 border-slate-200 text-slate-500"
                        }`}
                        title={sub.has_inputs === true ? "Submitted with inputs" : sub.has_inputs === false ? "No inputs" : "Pending"}
                      >
                        <span className="font-medium">{sub.user_name}</span>
                        {sub.user_username && <span className="text-slate-400 ml-1">@{sub.user_username}</span>}
                        <span className="ml-2">{getSubmissionStatusBadge(sub)}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Show detailed submissions */}
                  {showSubmissions && (
                    <div className="space-y-3 mt-4">
                      {submissions.submissions.filter(s => s.has_inputs && s.content).map((sub) => (
                        <div key={sub.user_id} className="border rounded-lg p-3 bg-red-50 border-red-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm text-slate-700">
                              {sub.user_name}
                              {sub.user_username && <span className="text-slate-400 ml-1">@{sub.user_username}</span>}
                            </span>
                            <span className="text-xs text-slate-500">
                              {sub.submitted_at && new Date(sub.submitted_at).toLocaleString()}
                            </span>
                          </div>
                          <ScrollArea className="h-32 bg-white rounded p-2">
                            <pre className="whitespace-pre-wrap text-xs text-slate-700 font-sans">
                              {sub.content}
                            </pre>
                          </ScrollArea>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Consolidation Button (only for creator) */}
                  {!submissions.consolidation_triggered && meeting.creator_id === folder?.creator_id && (
                    <Button
                      onClick={triggerConsolidation}
                      className="w-full mt-3 bg-blue-500 hover:bg-blue-600"
                    >
                      <Bot className="w-4 h-4 mr-2" />
                      Ask MeetAI to Consolidate Now
                    </Button>
                  )}
                </div>
              )}

              {/* Consolidated Minutes (after consolidation) */}
              {consolidatedMinutes?.consolidated_content && (
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-slate-700">Consolidated Minutes</p>
                    <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">By MeetAI</span>
                  </div>
                  <ScrollArea className="h-64 border rounded-lg p-4 bg-blue-50">
                    <div className="prose prose-sm max-w-none">
                      <pre className="whitespace-pre-wrap text-xs text-slate-700 font-sans leading-relaxed">
                        {consolidatedMinutes.consolidated_content}
                      </pre>
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Legacy minutes input for review period */}
              {minutes && minutes.status === "draft" && (
                <div className="space-y-2 border-t pt-4">
                  <Label className="text-sm">Submit your input/corrections (Legacy)</Label>
                  <Textarea
                    value={minutesInput}
                    onChange={(e) => setMinutesInput(e.target.value)}
                    placeholder="Add corrections, missing items, or comments..."
                    className="h-20"
                    data-testid="minutes-input"
                  />
                  <Button
                    onClick={() => submitMinutesInput(!!minutesInput.trim())}
                    disabled={!minutesInput.trim()}
                    className="w-full bg-violet-500 hover:bg-violet-600"
                    data-testid="submit-minutes-input-btn"
                  >
                    Submit Input
                  </Button>
                </div>
              )}

              {/* Attendance */}
              {minutes && (minutes.attendees?.length > 0 || minutes.apologies?.length > 0) && (
                <div className="border-t pt-3">
                  <p className="text-xs font-medium text-slate-600 mb-2">Attendance</p>
                  <div className="flex flex-wrap gap-2">
                    {minutes.attendees?.map((a, i) => (
                      <span key={i} className="bg-green-50 text-green-700 text-xs px-2 py-1 rounded">
                        {a.name}
                      </span>
                    ))}
                    {minutes.apologies?.map((a, i) => (
                      <span key={i} className="bg-red-50 text-red-700 text-xs px-2 py-1 rounded">
                        {a.name} (apology)
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
